import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { Tab, TabList, TabPanel, Tabs } from '@repo/components/tabs';

import { ContentHeader } from '~/features/content-header/content-header.tsx';
import {
  queryProjectBases,
  queryProjectGeneralNote,
  queryProjectSchema,
} from '~/services/composition/composition.ts';
import { queryLogoUrl } from '~/services/declaration/declaration.ts';

import { BasesPanel } from './-components/bases-panel.tsx';
import { GeneralNotePanel } from './-components/general-note-panel.tsx';
import { LogoPanel } from './-components/logo-panel.tsx';
import { SchemaPanel } from './-components/schema-panel.tsx';

const tabSchema = z.enum(['schema', 'bases', 'general-note', 'logo']).catch('schema');
type TabKey = z.infer<typeof tabSchema>;

const searchSchema = z.object({
  tab: tabSchema,
});

export const Route = createFileRoute('/project/$project/(composition-admin)/composition/setup')({
  validateSearch: searchSchema.catch(() => searchSchema.parse({ tab: 'schema' })),
  loader: async ({ context: { queryClient }, params: { project } }) => {
    // Prefetch in parallel so tab switching is instant.
    await Promise.all([
      queryClient.ensureQueryData(queryProjectSchema(project)),
      queryClient.ensureQueryData(queryProjectBases(project)),
      queryClient.ensureQueryData(queryProjectGeneralNote(project)),
      queryClient.ensureQueryData(queryLogoUrl(project)),
    ]);
  },
  component: CompositionSetupPage,
});

function CompositionSetupPage() {
  const { project: projectId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();

  const setTab = (next: TabKey) => {
    void navigate({ search: prev => ({ ...prev, tab: next }), replace: true, resetScroll: false });
  };

  return (
    <div className="max-w-5xl mx-auto flex flex-col gap-6 pb-8">
      <div>
        <ContentHeader>Composition setup</ContentHeader>
        <p className="text-sm text-secondary-500 mt-1">
          Everything that shapes how your project's composition declarations look and behave —
          the schema (components and detection limits), analysis bases, the general note that
          appears on every declaration, and the logo used on declaration PDFs.
        </p>
      </div>

      <Tabs
        selectedKey={tab}
        onSelectionChange={(key) => { setTab(key as TabKey); }}
      >
        <TabList aria-label="Composition setup sections">
          <Tab id="schema">Schema</Tab>
          <Tab id="bases">Bases</Tab>
          <Tab id="general-note">General note</Tab>
          <Tab id="logo">Logo</Tab>
        </TabList>

        <TabPanel id="schema" className="pt-2">
          <SchemaPanel projectId={projectId} />
        </TabPanel>
        <TabPanel id="bases" className="pt-2">
          <BasesPanel projectId={projectId} />
        </TabPanel>
        <TabPanel id="general-note" className="pt-2">
          <GeneralNotePanel projectId={projectId} />
        </TabPanel>
        <TabPanel id="logo" className="pt-2">
          <LogoPanel projectId={projectId} />
        </TabPanel>
      </Tabs>
    </div>
  );
}
